import React, { useState, useEffect, useRef } from "react";
import Logo from "../assets/Logo.png";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, User, Menu, X, LogOut, UserCircle } from "lucide-react";
import { useDoctor } from "../context/DoctorContext";

const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isScheduleActive = location.pathname.startsWith("/home");
  const { doctor, logout } = useDoctor();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const linkClass = ({ isActive }) =>
    isActive
      ? "text-[#fe8235] font-semibold"
      : "text-gray-900 hover:text-orange-400";

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Handle click outside dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Auto-close sidebar if screen is lg or above
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setSidebarOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleProfileClick = () => {
    setDropdownOpen(false);
    navigate("/profile");
  };

  return (
    <>
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm transition-all duration-300"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      {/* Sidebar for mobile */}
      <div
        className={`fixed top-0 left-0 h-full w-64 sm:w-80 md:w-96 bg-white z-50 shadow-lg transform transition-transform duration-300 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } flex flex-col`}
      >
        {/* Logo and close button */}
        <div className="flex items-center justify-between px-6 py-6 border-b">
          <div className="flex items-center space-x-3">
            <img
              src={Logo}
              alt="ReZen Logo"
              className="h-8 sm:h-10 md:h-12 w-8 sm:w-10 md:w-12"
            />
            <span className="text-lg sm:text-xl md:text-2xl font-bold">
              ReZen
            </span>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="text-[#fe8235] p-1 rounded hover:bg-orange-50 transition-colors"
          >
            <X className="h-7 w-7" />
          </button>
        </div>
        {/* Welcome Doctor section */}
        <div className="flex items-center gap-3 px-6 py-4 border-b">
          {doctor?.profileImage ? (
            <img
              src={doctor.profileImage}
              alt={doctor.name || "Doctor profile"}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              <User className="h-5 w-5 text-gray-600" />
            </div>
          )}
          <span className="text-sm text-gray-700 font-medium">
            Welcome, {doctor?.name || "Doctor"}
          </span>
        </div>
        {/* Nav links */}
        <nav className="flex flex-col gap-4 px-6 py-6">
          <NavLink
            to="/home"
            className={
              isScheduleActive
                ? "text-[#fe8235] font-semibold"
                : "text-gray-900 hover:text-orange-400"
            }
            onClick={() => setSidebarOpen(false)}
          >
            Home
          </NavLink>
          <NavLink
            to="/dashboard"
            className={linkClass}
            onClick={() => setSidebarOpen(false)}
          >
            Dashboard
          </NavLink>
          <NavLink
            to="/community"
            className={linkClass}
            onClick={() => setSidebarOpen(false)}
          >
            Community
          </NavLink>
        </nav>
        {/* Add Profile and Logout buttons to mobile menu */}
        <div className="mt-auto border-t">
          <button
            onClick={handleProfileClick}
            className="flex items-center gap-3 px-6 py-3 w-full hover:bg-gray-50 text-gray-700"
          >
            <UserCircle className="h-5 w-5" />
            <span>Profile</span>
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-6 py-3 w-full hover:bg-gray-50 text-red-600"
          >
            <LogOut className="h-5 w-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
      {/* Main Navbar */}
      <nav className="flex items-center justify-between padding-x py-7 border-b shadow-sm font-sora relative z-30">
        <div className="flex items-center space-x-4">
          <img
            src={Logo}
            alt="ReZen Logo"
            className="h-8 sm:h-10 md:h-12 w-8 sm:w-10 md:w-12"
          />
          <h1 className="text-lg sm:text-xl md:text-2xl font-bold">ReZen</h1>
        </div>
        {/* Desktop nav links */}
        <div className="hidden lg:flex space-x-8">
          <NavLink
            to="/home"
            className={
              isScheduleActive
                ? "text-[#fe8235] font-semibold"
                : "text-gray-900 hover:text-orange-400"
            }
          >
            Home
          </NavLink>
          <NavLink to="/dashboard" className={linkClass}>
            Dashboard
          </NavLink>
          <NavLink to="/community" className={linkClass}>
            Community
          </NavLink>
        </div>
        {/* Desktop user section */}
        <div className="hidden lg:flex items-center space-x-6">
          <div className="relative cursor-pointer">
            <MessageCircle className="h-6 w-6 text-gray-800" />
            <span className="absolute -top-1 -right-1.5 bg-orange-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
              3
            </span>
          </div>
          <div className="relative" ref={dropdownRef}>
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <span className="text-sm text-gray-700">
                Welcome, {doctor?.name || "Doctor"}
              </span>
              {doctor?.profileImage ? (
                <img
                  src={doctor.profileImage}
                  alt={doctor.name || "Doctor profile"}
                  className="h-8 w-8 rounded-full object-cover"
                />
              ) : (
                <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <User className="h-5 w-5 text-gray-600" />
                </div>
              )}
            </div>

            {/* Dropdown Menu */}
            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 border">
                <button
                  onClick={handleProfileClick}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
                >
                  <UserCircle className="h-5 w-5" />
                  <span>Profile</span>
                </button>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-gray-50 w-full"
                >
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </div>
            )}
          </div>
        </div>
        {/* Hamburger for mobile */}
        <button
          className="lg:hidden flex items-center justify-center p-2 rounded focus:outline-none hover:bg-orange-50"
          onClick={() => setSidebarOpen(true)}
        >
          <Menu className="h-8 w-8 text-[#fe8235]" />
        </button>
      </nav>
    </>
  );
};

export default Navbar;
